import { Box, Container, Typography, Button, Chip } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import VerifiedIcon from "@mui/icons-material/Verified";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ShieldIcon from "@mui/icons-material/Shield";
import LockIcon from "@mui/icons-material/Lock";
import BoltIcon from "@mui/icons-material/Bolt";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";

const GOLD      = "#D97706";
const GOLD_GRAD = "linear-gradient(135deg,#D97706,#F59E0B)";

const STATS = [
  { value: "50K+",  label: "Entities Screened" },
  { value: "200+",  label: "Sanctions Lists"   },
  { value: "99.9%", label: "Platform Uptime"   },
  { value: "48h",   label: "Go-Live Time"      },
];

const FLOATERS = [
  { top: "10%", right: "2%", delay: "0s",   Icon: NotificationsActiveIcon, accent: "#16A34A", title: "Sanctions Hit", sub: "OFAC SDN · Auto-flagged" },
  { top: "48%", right: "0%", delay: "1.6s", Icon: TrendingUpIcon,          accent: "#EA580C", title: "Risk Score: High", sub: "Customer #4821 · Review" },
  { top: "78%", right: "14%",delay: "0.8s", Icon: CheckCircleIcon,         accent: "#2563EB", title: "KYC Cleared", sub: "3 entities · Just now" },
];

const TRUST = [
  { Icon: ShieldIcon,   label: "FATF Aligned"        },
  { Icon: LockIcon,     label: "ISO 27001 Practices" },
  { Icon: BoltIcon,     label: "Real-Time Screening" },
  { Icon: VerifiedIcon, label: "FIU-Ready Reports"   },
];

export default function Hero({ onRequestDemo }) {
  return (
    <Box
      id="home"
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(160deg, #F8FAFC 0%, #ffffff 55%, #FEF9EC 100%)",
        position: "relative",
        display: "flex",
        alignItems: "center",
        overflow: "hidden",
        pt: { xs: 14, md: 10 },
        pb: { xs: 8, md: 6 },
      }}
    >
      {/* Subtle grid */}
      <Box sx={{
        position: "absolute", inset: 0, zIndex: 0, pointerEvents: "none",
        backgroundImage:
          "linear-gradient(#E2E8F0 1px, transparent 1px)," +
          "linear-gradient(90deg, #E2E8F0 1px, transparent 1px)",
        backgroundSize: "64px 64px",
        opacity: .35,
        maskImage: "radial-gradient(ellipse 70% 70% at 50% 30%, black 30%, transparent 100%)",
      }} />

      {/* Gold ambient glow top-right */}
      <Box sx={{
        position: "absolute", top: "-10%", right: "-5%", width: "40%", height: "60%",
        background: "radial-gradient(ellipse, rgba(217,119,6,.08) 0%, transparent 70%)",
        pointerEvents: "none", zIndex: 0,
      }} />

      <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1 }}>
        <Box sx={{
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          alignItems: "center",
          gap: { xs: 8, lg: 8 },
        }}>

          {/* ── Left ── */}
          <Box sx={{ flex: "0 0 52%", maxWidth: { lg: "52%" } }}>
            <Chip
              icon={<VerifiedIcon sx={{ fontSize: "13px !important", color: `${GOLD} !important` }} />}
              label="FATF-aligned · UAE CBUAE Compliant"
              size="small"
              sx={{
                mb: 3.5,
                background: "rgba(217,119,6,.08)",
                border: "1px solid rgba(217,119,6,.2)",
                color: GOLD,
                fontWeight: 600, fontSize: 12,
                borderRadius: "20px",
                fontFamily: "IBM Plex Sans, sans-serif",
                animation: "fadeInUp .5s ease both",
              }}
            />

            <Typography
              component="h1"
              className="font-display"
              sx={{
                fontSize: { xs: "38px", sm: "50px", md: "62px", xl: "70px" },
                fontWeight: 700,
                lineHeight: 1.06,
                letterSpacing: "-2px",
                color: "#0F172A",
                mb: 3,
                animation: "fadeInUp .5s .08s ease both",
              }}
            >
              AML Compliance
              <br />
              <Box
                component="span"
                sx={{
                  background: GOLD_GRAD,
                  backgroundSize: "200% auto",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  animation: "shimmer 3.5s linear infinite",
                }}
              >
                built for DNFBPs
              </Box>
            </Typography>

            <Typography sx={{
              fontSize: { xs: 16, md: 18 },
              color: "#64748B",
              lineHeight: 1.8,
              maxWidth: 520,
              mb: 5,
              fontFamily: "IBM Plex Sans, sans-serif",
              animation: "fadeInUp .5s .16s ease both",
            }}>
              Automate KYC onboarding, PEP &amp; sanctions screening, risk profiling and regulatory reporting — purpose-built for DNFBPs operating under UAE and global AML-CFT frameworks.
            </Typography>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 6, animation: "fadeInUp .5s .24s ease both" }}>
              <Button
                onClick={onRequestDemo}
                endIcon={<ArrowForwardIcon />}
                sx={{
                  px: 4, py: 1.75, borderRadius: "10px",
                  fontSize: 15, fontWeight: 600, textTransform: "none",
                  fontFamily: "IBM Plex Sans, sans-serif",
                  background: GOLD_GRAD, color: "#fff",
                  boxShadow: "0 6px 24px rgba(217,119,6,.35)",
                  "&:hover": {
                    background: "linear-gradient(135deg,#B45309,#D97706)",
                    transform: "translateY(-2px)",
                    boxShadow: "0 10px 32px rgba(217,119,6,.45)",
                  },
                  transition: "all .22s",
                }}
              >
                Get a Free Demo
              </Button>
              <Button
                href="/mctrl/login"
                startIcon={<PlayArrowIcon />}
                sx={{
                  px: 4, py: 1.75, borderRadius: "10px",
                  fontSize: 15, fontWeight: 500, textTransform: "none",
                  fontFamily: "IBM Plex Sans, sans-serif",
                  color: "#475569",
                  border: "1px solid #CBD5E1",
                  "&:hover": { border: "1px solid #94A3B8", background: "#F8FAFC", color: "#0F172A" },
                  transition: "all .2s",
                }}
              >
                Sign In to Platform
              </Button>
            </Box>

            {/* Trust badges */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, animation: "fadeInUp .5s .32s ease both" }}>
              {TRUST.map(({ Icon, label }) => (
                <Box key={label} sx={{
                  display: "flex", alignItems: "center", gap: 1,
                  px: 1.75, py: .85, borderRadius: "8px",
                  background: "#F8FAFC", border: "1px solid #E2E8F0",
                }}>
                  <Icon sx={{ fontSize: 13, color: "#16A34A" }} />
                  <Typography sx={{ fontSize: 12, fontWeight: 500, color: "#64748B", fontFamily: "IBM Plex Sans, sans-serif" }}>
                    {label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* ── Right — stats + floating cards ── */}
          <Box sx={{ flex: 1, position: "relative", minHeight: { xs: 340, lg: 480 }, width: "100%" }}>

            {/* Stats bento */}
            <Box sx={{
              display: "grid", gridTemplateColumns: "1fr 1fr",
              gap: 2, maxWidth: 400, mx: "auto",
              animation: "fadeInUp .5s .2s ease both",
            }}>
              {STATS.map(s => (
                <Box key={s.label} sx={{
                  background: "#fff",
                  border: "1px solid #E2E8F0",
                  borderRadius: "16px",
                  p: { xs: 3, md: 3.5 },
                  boxShadow: "0 2px 12px rgba(15,23,42,.06)",
                  transition: "all .22s",
                  "&:hover": {
                    borderColor: "#FCD34D",
                    boxShadow: "0 8px 28px rgba(217,119,6,.12)",
                    transform: "translateY(-3px)",
                  },
                }}>
                  <Typography sx={{
                    fontSize: { xs: 34, md: 42 },
                    fontWeight: 700,
                    fontFamily: "IBM Plex Sans, sans-serif",
                    background: GOLD_GRAD,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    lineHeight: 1, mb: .75,
                  }}>
                    {s.value}
                  </Typography>
                  <Typography sx={{ fontSize: 12, fontWeight: 500, color: "#94A3B8", fontFamily: "IBM Plex Sans, sans-serif" }}>
                    {s.label}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Floating notification cards */}
            {FLOATERS.map(f => (
              <Box
                key={f.title}
                sx={{
                  position: { xs: "relative", lg: "absolute" },
                  display: { xs: "none", lg: "flex" },
                  top: f.top, right: f.right,
                  alignItems: "center", gap: 1.5,
                  background: "#fff",
                  border: "1px solid #E2E8F0",
                  borderRadius: "14px",
                  px: 2.5, py: 1.75,
                  minWidth: 220,
                  animation: `floatY 4.5s ${f.delay} ease-in-out infinite`,
                  boxShadow: "0 8px 32px rgba(15,23,42,.1)",
                  zIndex: 10,
                }}
              >
                <Box sx={{
                  width: 36, height: 36, borderRadius: "10px",
                  background: `${f.accent}14`,
                  border: `1px solid ${f.accent}28`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0,
                }}>
                  <f.Icon sx={{ fontSize: 18, color: f.accent }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#0F172A", lineHeight: 1.3, fontFamily: "IBM Plex Sans, sans-serif" }}>
                    {f.title}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: "#94A3B8", fontFamily: "IBM Plex Sans, sans-serif" }}>
                    {f.sub}
                  </Typography>
                </Box>
                <Box sx={{
                  width: 8, height: 8, borderRadius: "50%",
                  background: f.accent, flexShrink: 0,
                  animation: "pulseRing 2s ease infinite",
                }} />
              </Box>
            ))}
          </Box>
        </Box>

        {/* Regulatory alignment strip */}
        <Box sx={{
          mt: { xs: 8, md: 10 },
          pt: 4,
          borderTop: "1px solid #F1F5F9",
          animation: "fadeInUp .5s .4s ease both",
        }}>
          <Typography sx={{
            fontSize: 11, fontWeight: 600, color: "#94A3B8",
            letterSpacing: "2.5px", textTransform: "uppercase",
            mb: 3.5, textAlign: "center",
            fontFamily: "IBM Plex Sans, sans-serif",
          }}>
            Aligned with leading AML regulatory frameworks
          </Typography>
          <Box sx={{
            display: "flex", flexWrap: "wrap",
            justifyContent: "center", gap: 2,
          }}>
            {[
              { abbr: "FATF",  name: "Financial Action Task Force",      dot: "#2563EB" },
              { abbr: "CBUAE", name: "Central Bank of the UAE",          dot: "#16A34A" },
              { abbr: "FIU",   name: "UAE Financial Intelligence Unit",  dot: "#D97706" },
              { abbr: "VARA",  name: "Virtual Assets Regulatory Auth.",  dot: "#7C3AED" },
              { abbr: "RERA",  name: "Real Estate Regulatory Agency",    dot: "#0891B2" },
              { abbr: "MOE",   name: "UAE Ministry of Economy",          dot: "#DC2626" },
            ].map(({ abbr, name, dot }) => (
              <Box key={abbr} sx={{
                display: "flex", alignItems: "center", gap: 1.5,
                px: 2, py: 1.1,
                borderRadius: "10px",
                background: "#fff",
                border: "1px solid #E2E8F0",
                boxShadow: "0 1px 4px rgba(15,23,42,.05)",
              }}>
                <Box sx={{
                  width: 7, height: 7, borderRadius: "50%",
                  background: dot, flexShrink: 0,
                }} />
                <Typography sx={{
                  fontSize: 12, fontWeight: 700, color: "#0F172A",
                  fontFamily: "IBM Plex Sans, sans-serif", lineHeight: 1,
                }}>
                  {abbr}
                </Typography>
                <Typography sx={{
                  fontSize: 11, color: "#94A3B8",
                  fontFamily: "IBM Plex Sans, sans-serif",
                  display: { xs: "none", sm: "block" },
                }}>
                  {name}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
